/**
 * Runtime application configuration.
 *
 * ## Why this exists
 *
 * The Angular bundle used to be compiled once per environment
 * (`ng build --configuration recette`, `--configuration staging`, …), which meant
 * one Docker image per environment. Four images from one commit means the
 * artifact validated in recette is not the artifact shipped to production.
 *
 * Everything that genuinely differs between environments now arrives at runtime
 * from `GET /config`, served by the .NET host from its ECS environment
 * variables. The bundle is therefore environment-agnostic and one image is
 * promoted unchanged from dev to production.
 *
 * Build-time constants that are identical in every environment (role names, the
 * Entra ID authority, the relative API path) stay in `environments/environment.ts`.
 *
 * ## Why it is not an Angular service
 *
 * MSAL's `PublicClientApplication` is constructed by a provider factory during
 * module instantiation, so its configuration must be readable *synchronously*.
 * An `APP_INITIALIZER` runs too late. Hence a plain module-level holder that
 * `main.ts` populates before `bootstrapModule`.
 */

/** The values that genuinely differ between deployed environments. */
export interface RuntimeConfig {
  /** Environment label: `dev` | `rec` | `stg` | `prd`. */
  name: string;
  /** Enables Angular production mode and read-only NgRx devtools. */
  production: boolean;
  /** Cognito user pool backing the user-sharing lookups. */
  userPoolId: string;
  /** Entra ID application (client) id — one registration per environment. */
  msalClientId: string;
  /** Delegated scopes for the Portail Info API — one API registration per environment. */
  apiScopes: string[];
}

/**
 * Used only when the app is served by `ng serve` with no .NET host behind it.
 *
 * `ng serve` answers unknown paths with `index.html`, so a missing `/config`
 * surfaces as HTML rather than a network error — hence the content check in
 * `loadRuntimeConfig`. Outside localhost a missing or malformed `/config` is a
 * hard failure: booting with the wrong Entra ID client id would produce a
 * confusing authentication loop instead of an obvious error.
 */
const LOCAL_DEV_CONFIG: RuntimeConfig = {
  name: "local",
  production: false,
  userPoolId: "eu-west-1_efaMZiBKo",
  msalClientId: "b9f56a4d-00c1-474b-9bc1-0f4404a0c2fd",
  apiScopes: ["api://f337e73d-aba8-49da-a72b-4caa0407e159/access_as_user"],
};

let current: RuntimeConfig | null = null;

/** Populated by `main.ts` before the application bootstraps. */
export function setRuntimeConfig(config: RuntimeConfig): void {
  if (!isRuntimeConfig(config)) throw new Error("Invalid public runtime configuration");
  current = config;
}

/**
 * The active runtime configuration.
 *
 * Throws when called before `main.ts` has loaded it, which would otherwise show
 * up as a silent misconfiguration much later.
 */
export function runtimeConfig(): RuntimeConfig {
  if (!current) {
    throw new Error(
      "Runtime configuration was read before it was loaded. It must be fetched in main.ts before bootstrapModule().",
    );
  }
  return current;
}

/** Origin the app is served from — the basis of every redirect URI. */
export function appOrigin(): string {
  return window.location.origin;
}

function isLocalhost(): boolean {
  return ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);
}

export function isRuntimeConfig(value: unknown): value is RuntimeConfig {
  const candidate = value as Partial<RuntimeConfig> | null;
  return (
    !!candidate &&
    typeof candidate.name === "string" && candidate.name.trim().length > 0 &&
    typeof candidate.production === "boolean" &&
    typeof candidate.userPoolId === "string" && candidate.userPoolId.trim().length > 0 &&
    typeof candidate.msalClientId === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(candidate.msalClientId) &&
    Array.isArray(candidate.apiScopes) && candidate.apiScopes.length > 0 &&
    candidate.apiScopes.every(scope => typeof scope === "string" && scope.trim().length > 0)
  );
}

/**
 * Fetch `/config` from the hosting server.
 *
 * Falls back to `LOCAL_DEV_CONFIG` on localhost only; anywhere else a failure is
 * rethrown so the deployment fails visibly rather than running with the wrong
 * identity configuration.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const response = await fetch("/config", { cache: "no-store" });
    if (!response.ok) {
      throw new Error(`GET /config returned ${response.status}`);
    }

    const payload: unknown = await response.json();
    if (!isRuntimeConfig(payload)) {
      throw new Error("GET /config returned an unexpected payload shape");
    }

    return payload;
  } catch (error) {
    if (isLocalhost()) {
      console.warn(
        "Could not load /config; falling back to the local development configuration. " +
          "Start the .NET host to serve the real values.",
        error,
      );
      return LOCAL_DEV_CONFIG;
    }
    throw error;
  }
}
